/** Typed wrappers for /v2/console/auth/mfa/* endpoints. */
import { client, apiPost, apiGet, apiDelete } from './client';
import type { LoginResponse200 } from './auth';

export type MFAMethod = 'totp' | 'sms' | 'email' | 'backup_code';

export interface MFADeviceSummary {
  id: number;
  kind: 'totp' | 'sms' | 'email';
  name: string;
  target_masked?: string;
  confirmed: boolean;
}

export async function listDevices(enrollmentToken?: string): Promise<{
  devices: MFADeviceSummary[];
  primary_method: 'totp' | 'sms' | 'email';
}> {
  return apiGet('/auth/mfa/devices/', enrollmentToken ? withEnrollment(enrollmentToken) : undefined);
}

export async function setPrimary(deviceId: number): Promise<void> {
  await apiPost(`/auth/mfa/devices/${deviceId}/set_primary/`);
}

export async function deleteDevice(deviceId: number): Promise<void> {
  await apiDelete(`/auth/mfa/devices/${deviceId}/`);
}

export async function challenge(
  mfaPendingToken: string,
  method: 'totp' | 'sms' | 'email',
): Promise<{ ok?: boolean; sent_to_masked?: string | null }> {
  const r = await client.post('/auth/mfa/challenge/', {
    mfa_pending_token: mfaPendingToken,
    method,
  });
  return r.data ?? {};
}

export async function verify(
  mfaPendingToken: string,
  method: MFAMethod,
  code: string,
): Promise<LoginResponse200> {
  return apiPost('/auth/mfa/verify/', { mfa_pending_token: mfaPendingToken, method, code });
}

export async function enrollTotp(
  enrollmentToken?: string,
  name = 'Authenticator',
): Promise<{ device_id: number; secret: string; provisioning_uri: string; qr: string }> {
  return apiPost('/auth/mfa/enroll/totp/', { name }, withEnrollment(enrollmentToken));
}

export async function confirmTotp(
  deviceId: number,
  code: string,
  enrollmentToken?: string,
): Promise<{ device_id: number; confirmed: boolean }> {
  return apiPost(
    '/auth/mfa/enroll/totp/confirm/',
    { device_id: deviceId, code },
    withEnrollment(enrollmentToken),
  );
}

export async function enrollSms(
  phone: string,
  enrollmentToken?: string,
): Promise<{ device_id: number; sent_to_masked: string }> {
  return apiPost('/auth/mfa/enroll/sms/', { phone }, withEnrollment(enrollmentToken));
}

export async function confirmSms(
  deviceId: number,
  code: string,
  enrollmentToken?: string,
): Promise<{ device_id: number; confirmed: boolean }> {
  return apiPost(
    '/auth/mfa/enroll/sms/confirm/',
    { device_id: deviceId, code },
    withEnrollment(enrollmentToken),
  );
}

export async function enrollEmail(
  email: string,
  enrollmentToken?: string,
): Promise<{ device_id: number; sent_to_masked: string }> {
  return apiPost('/auth/mfa/enroll/email/', { email }, withEnrollment(enrollmentToken));
}

export async function confirmEmail(
  deviceId: number,
  code: string,
  enrollmentToken?: string,
): Promise<{ device_id: number; confirmed: boolean }> {
  return apiPost(
    '/auth/mfa/enroll/email/confirm/',
    { device_id: deviceId, code },
    withEnrollment(enrollmentToken),
  );
}

export async function regenerateBackupCodes(enrollmentToken?: string): Promise<{
  codes: string[];
  count: number;
}> {
  return apiPost(
    '/auth/mfa/backup_codes/regenerate/',
    undefined,
    withEnrollment(enrollmentToken),
  );
}

function withEnrollment(token?: string) {
  return token ? { headers: { 'X-Enrollment-Token': token } } : undefined;
}
